import { useState, useEffect, useMemo, type CSSProperties, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import type { Language } from '@/i18n/types';

function useIsDark() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

/* ─── Types ─── */
type VisualType = 'petal' | 'heart' | 'sparkle' | 'letter';
type Side = 'left' | 'right';

interface VisualFragment {
  id: string;
  type: VisualType;
  side: Side;
  xPct: number;
  yPct: number;
  size: number;
  delay: number;
  duration: number;
  drift: number;
  rotation: number;
}

interface MessageFragment {
  id: string;
  text: string;
  side: Side;
  xPct: number;
  yPct: number;
  delay: number;
  duration: number;
  drift: number;
  baseOpacity: number;
}

/* ─── Data ─── */
const MESSAGE_POOL: Record<Language, string[]> = {
  en: [
    'To: Dad', 'I never said thank you.', 'You mattered more than I knew.',
    "I'm sorry for how I left things.", 'There are things I should have said sooner.',
    'I love you.', 'You were always there.', 'I still think about that day.',
    'Thank you for carrying me.', 'I wish I had said this earlier.',
  ],
  es: [
    'Para: Papá', 'Nunca dije gracias.', 'Significaste más de lo que sabía.',
    'Perdón por cómo dejé las cosas.', 'Hay cosas que debí decir antes.',
    'Te quiero.', 'Siempre estuviste ahí.', 'Aún pienso en ese día.',
    'Gracias por cargar conmigo.', 'Ojalá lo hubiera dicho antes.',
  ],
  pt: [
    'Para: Pai', 'Eu nunca disse obrigado.', 'Você importou mais do que eu sabia.',
    'Desculpa por como deixei as coisas.', 'Há coisas que eu deveria ter dito antes.',
    'Eu te amo.', 'Você sempre esteve lá.', 'Ainda penso naquele dia.',
    'Obrigado por me carregar.', 'Queria ter dito isso antes.',
  ],
  fr: [
    'À : Papa', 'Je n\'ai jamais dit merci.', 'Tu comptais plus que je ne le savais.',
    'Pardon pour la façon dont j\'ai laissé les choses.', 'Il y a des choses que j\'aurais dû dire plus tôt.',
    'Je t\'aime.', 'Tu étais toujours là.', 'Je pense encore à ce jour-là.',
    'Merci de m\'avoir porté.', 'J\'aurais aimé le dire plus tôt.',
  ],
  ar: [
    'إلى: أبي', 'لم أقل شكرًا أبدًا.', 'كنت أهم مما كنت أعرف.',
    'آسف على الطريقة التي تركت بها الأمور.', 'هناك أشياء كان يجب أن أقولها قبل ذلك.',
    'أحبك.', 'لقد كنت دائمًا هناك.', 'ما زلت أفكر في ذلك اليوم.',
    'شكرًا لحملك لي.', 'أتمنى لو قلت هذا قبل ذلك.',
  ],
  ja: [
    'お父さんへ', 'ありがとうを言えなかった。', 'あなたは私が思っていた以上に大切でした。',
    'あの時の去り方を後悔しています。', 'もっと早く言うべきことがありました。',
    '愛しています。', 'いつもそばにいてくれた。', 'あの日のことをまだ考えています。',
    '支えてくれてありがとう。', 'もっと早く言いたかった。',
  ],
  ko: [
    '아빠에게', '감사하다는 말을 못 했어요.', '당신은 제가 알았던 것보다 더 소중했어요.',
    '그렇게 떠난 것 미안해요.', '더 일찍 말했어야 할 것들이 있어요.',
    '사랑해요.', '항상 거기 있어 주셨죠.', '아직도 그날을 생각해요.',
    '저를 지탱해 줘서 고마워요.', '더 일찍 말했으면 좋았을 텐데.',
  ],
  id: [
    'Untuk: Ayah', 'Aku tak pernah bilang terima kasih.', 'Kamu lebih berarti dari yang aku tahu.',
    'Maaf atas cara aku pergi.', 'Ada hal-hal yang seharusnya aku katakan lebih awal.',
    'Aku mencintaimu.', 'Kamu selalu ada di sana.', 'Aku masih memikirkan hari itu.',
    'Terima kasih telah menopangku.', 'Andai aku mengatakannya lebih awal.',
  ],
  hi: [
    'पिताजी के लिए', 'मैंने कभी धन्यवाद नहीं कहा।', 'आप मेरी सोच से ज़्यादा मायने रखते थे।',
    'मैंने जैसे चीज़ें छोड़ीं उसके लिए माफ़ करें।', 'कुछ बातें मुझे पहले कहनी चाहिए थीं।',
    'मैं आपसे प्यार करता/करती हूं।', 'आप हमेशा वहां थे।', 'मैं अभी भी उस दिन के बारे में सोचता/सोचती हूं।',
    'मुझे संभालने के लिए धन्यवाद।', 'काश मैंने यह पहले कहा होता।',
  ],
};

/* ─── Generators ─── */
function generateVisuals(count: number): VisualFragment[] {
  const types: VisualType[] = ['petal', 'heart', 'sparkle', 'letter'];
  return Array.from({ length: count }, (_, i) => ({
    id: `v-${i}`,
    type: types[i % types.length],
    side: (i % 2 === 0 ? 'left' : 'right') as Side,
    xPct: 10 + Math.random() * 80,
    yPct: 5 + Math.random() * 90,
    size: 12 + Math.random() * 20,
    delay: Math.random() * 8,
    duration: 8 + Math.random() * 6,
    drift: -25 + Math.random() * 50,
    rotation: Math.random() * 360,
  }));
}

function generateMessages(count: number, pool: string[]): MessageFragment[] {
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count).map((text, i) => ({
    id: `m-${i}`,
    text,
    side: (i % 2 === 0 ? 'left' : 'right') as Side,
    xPct: 4 + Math.random() * 14,
    yPct: 8 + (i / count) * 80 + Math.random() * 10,
    delay: 2 + Math.random() * 10,
    duration: 12 + Math.random() * 6,
    drift: -12 + Math.random() * 24,
    baseOpacity: 0.35 + Math.random() * 0.2,
  }));
}

/* ─── SVG Shapes ─── */
function VisualShape({ type, size }: { type: VisualType; size: number }) {
  switch (type) {
    case 'petal':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <ellipse cx="12" cy="12" rx="5" ry="10" fill="currentColor" opacity="0.6" transform="rotate(25 12 12)" />
          <ellipse cx="12" cy="12" rx="3.5" ry="7" fill="currentColor" opacity="0.3" transform="rotate(-15 12 12)" />
        </svg>
      );
    case 'heart':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" opacity="0.5">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
      );
    case 'sparkle':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" opacity="0.5">
          <circle cx="12" cy="12" r="2.5" />
          <circle cx="12" cy="12" r="6" opacity="0.15" />
        </svg>
      );
    case 'letter':
      return (
        <svg width={size * 1.2} height={size} viewBox="0 0 28 22" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.45">
          <rect x="1" y="1" width="26" height="20" rx="3" />
          <path d="M1 1l13 10L27 1" />
        </svg>
      );
  }
}

/* ─── Gutter wrapper ─── */
function Gutter({ side, children }: { side: Side; children: ReactNode }) {
  const style: CSSProperties = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    [side]: 0,
    width: 'calc((100vw - min(100%, 72rem)) / 2)',
    minWidth: '40px',
  };
  return <div style={style}>{children}</div>;
}

/* ─── Main Component ─── */
export function FloatingMessageFragments() {
  const [mode, setMode] = useState<'off' | 'tablet' | 'desktop'>('off');
  const isDark = useIsDark();
  const { language } = useLanguage();

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w >= 1440) setMode('desktop');
      else if (w >= 768) setMode('tablet');
      else setMode('off');
    };
    update();

    const desktop = window.matchMedia('(min-width: 1440px)');
    const tablet = window.matchMedia('(min-width: 768px) and (max-width: 1439px)');
    const handler = () => update();
    desktop.addEventListener('change', handler);
    tablet.addEventListener('change', handler);
    return () => {
      desktop.removeEventListener('change', handler);
      tablet.removeEventListener('change', handler);
    };
  }, []);

  const visualCount = mode === 'desktop' ? 16 : mode === 'tablet' ? 8 : 0;
  const messageCount = mode === 'desktop' ? 10 : mode === 'tablet' ? 5 : 0;
  const pool = MESSAGE_POOL[language] ?? MESSAGE_POOL.en;

  const visuals = useMemo(() => generateVisuals(visualCount), [visualCount]);
  const messages = useMemo(() => generateMessages(messageCount, pool), [messageCount, pool]);

  if (mode === 'off') return null;

  const leftVisuals = visuals.filter(f => f.side === 'left');
  const rightVisuals = visuals.filter(f => f.side === 'right');
  const leftMessages = messages.filter(f => f.side === 'left');
  const rightMessages = messages.filter(f => f.side === 'right');

  // Color + animation config based on theme
  const visualColorLeft = isDark ? 'hsl(var(--primary))' : 'hsl(var(--primary) / 0.7)';
  const visualColorRight = isDark ? 'hsl(var(--secondary))' : 'hsl(var(--secondary) / 0.7)';
  const msgColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.55)';

  const visualShadowLeft = isDark
    ? ['0 0 4px hsl(var(--primary) / 0.3)', '0 0 14px hsl(var(--primary) / 0.6)', '0 0 4px hsl(var(--primary) / 0.3)']
    : ['0 0 0px transparent', '0 0 3px hsl(var(--primary) / 0.25)', '0 0 0px transparent'];
  const visualShadowRight = isDark
    ? ['0 0 4px hsl(var(--secondary) / 0.3)', '0 0 14px hsl(var(--secondary) / 0.6)', '0 0 4px hsl(var(--secondary) / 0.3)']
    : ['0 0 0px transparent', '0 0 3px hsl(var(--secondary) / 0.25)', '0 0 0px transparent'];
  const msgShadowLeft = isDark
    ? ['0 0 4px hsl(var(--primary) / 0.2)', '0 0 12px hsl(var(--primary) / 0.5)', '0 0 4px hsl(var(--primary) / 0.2)']
    : ['0 0 0px transparent', '0 0 2px rgba(0,0,0,0.15)', '0 0 0px transparent'];
  const msgShadowRight = isDark
    ? ['0 0 4px hsl(var(--secondary) / 0.2)', '0 0 12px hsl(var(--secondary) / 0.5)', '0 0 4px hsl(var(--secondary) / 0.2)']
    : ['0 0 0px transparent', '0 0 2px rgba(0,0,0,0.15)', '0 0 0px transparent'];

  return (
    <div
      className="fixed inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 1 }}
      aria-hidden="true"
    >
      {/* Left gutter */}
      <Gutter side="left">
        {leftVisuals.map(f => (
          <motion.div
            key={f.id}
            className="absolute"
            style={{ left: `${f.xPct}%`, top: `${f.yPct}%`, color: visualColorLeft }}
            animate={{
              y: [0, -35, 15, -25, 0],
              x: [0, f.drift * 0.4, -f.drift * 0.3, f.drift * 0.2, 0],
              rotate: [f.rotation, f.rotation + 50, f.rotation + 20, f.rotation + 70, f.rotation],
              scale: [1, 1.08, 0.96, 1.05, 1],
              filter: visualShadowLeft.map(s => `drop-shadow(${s})`),
            }}
            transition={{ duration: f.duration, delay: f.delay, repeat: Infinity, ease: 'easeInOut' }}
          >
            <VisualShape type={f.type} size={f.size} />
          </motion.div>
        ))}
        {leftMessages.map(f => (
          <motion.span
            key={f.id}
            className="absolute inline-block text-[11px] leading-relaxed font-serif italic whitespace-normal break-words select-none"
            style={{
              left: `${f.xPct}%`,
              top: `${f.yPct}%`,
              maxWidth: 'calc(100% - 16px)',
              color: msgColor,
            }}
            animate={{
              y: [0, -14, 8, -10, 0],
              x: [0, f.drift * 0.25, -f.drift * 0.15, 0],
              scale: [1, 1.04, 0.98, 1.03, 1],
              textShadow: msgShadowLeft,
            }}
            transition={{ duration: f.duration, delay: f.delay, repeat: Infinity, ease: 'easeInOut' }}
          >
            {f.text}
          </motion.span>
        ))}
      </Gutter>

      {/* Right gutter */}
      <Gutter side="right">
        {rightVisuals.map(f => (
          <motion.div
            key={f.id}
            className="absolute"
            style={{ left: `${f.xPct}%`, top: `${f.yPct}%`, color: visualColorRight }}
            animate={{
              y: [0, 25, -20, 30, 0],
              x: [0, -f.drift * 0.3, f.drift * 0.4, -f.drift * 0.2, 0],
              rotate: [f.rotation, f.rotation - 40, f.rotation - 70, f.rotation - 30, f.rotation],
              scale: [1, 1.06, 0.97, 1.04, 1],
              filter: visualShadowRight.map(s => `drop-shadow(${s})`),
            }}
            transition={{ duration: f.duration, delay: f.delay, repeat: Infinity, ease: 'easeInOut' }}
          >
            <VisualShape type={f.type} size={f.size} />
          </motion.div>
        ))}
        {rightMessages.map(f => (
          <motion.span
            key={f.id}
            className="absolute inline-block text-[11px] leading-relaxed font-serif italic whitespace-normal break-words select-none text-right"
            style={{
              right: '4px',
              top: `${f.yPct}%`,
              maxWidth: 'calc(100% - 8px)',
              color: msgColor,
            }}
            animate={{
              y: [0, 12, -8, 10, 0],
              x: [0, -f.drift * 0.2, f.drift * 0.15, 0],
              scale: [1, 1.04, 0.98, 1.03, 1],
              textShadow: msgShadowRight,
            }}
            transition={{ duration: f.duration, delay: f.delay, repeat: Infinity, ease: 'easeInOut' }}
          >
            {f.text}
          </motion.span>
        ))}
      </Gutter>
    </div>
  );
}
